import { zodResolver } from "@hookform/resolvers/zod";
import { MenuItem, Typography, Button } from "@mui/material";
import { ReactElement } from "react";
import { useForm } from "react-hook-form";
import z from "zod";

import { AudienceContactTypeTypeDefinitions } from "@stw/audience-contact-fields";
import { STATES } from "@stw/states";

import {
  ImportCampaignContactsFromSubscribersMutation,
  ImportCampaignContactsFromSubscribersMutationVariables,
} from "~frontend/__generated__/graphql";
import { FlexBox } from "~frontend/components/FlexBox";
import {
  RhfSelect,
  RhfTextField,
} from "~frontend/components/forms/react-hook-form-components";
import { SUBSCRIBER_CAMPAIGN_CONTACT_IMPORT } from "~frontend/graphql/campaigns";
import theme from "~frontend/theme";
import useMutation from "~frontend/util/useMutation";

import { CampaignForEditor } from "../../types";

const ZipArraySchema = z.array(
  AudienceContactTypeTypeDefinitions.ZIP.valueSchema()
);

const LocationFilterSchema = z.object({
  filterSubscribers: z.boolean(),
  combineWith: z.enum(["include", "exclude"]),
  states: z.array(AudienceContactTypeTypeDefinitions.STATE.valueSchema()),
  zips: z.string().refine(v => {
    const parseResult = ZipArraySchema.safeParse(v.split(","));

    return parseResult.success;
  }, "Must be a comma-separated list of five-digit ZIP codes"),
});

export default function LegacySubscriberImport({
  campaign,
  readOnly,
}: {
  campaign: CampaignForEditor;
  readOnly?: boolean;
}): ReactElement {
  const [startImport] = useMutation<
    ImportCampaignContactsFromSubscribersMutation,
    ImportCampaignContactsFromSubscribersMutationVariables
  >(SUBSCRIBER_CAMPAIGN_CONTACT_IMPORT, {
    variables: {
      input: {
        campaignId: campaign.id,
      },
    },
    onErrorSnackbarMessage: "Failed to start subscriber import",
  });

  const defaultValues: {
    filterSubscribers: boolean;
    combineWith: "include" | "exclude";
    zips: string;
    states: string[];
  } = {
    filterSubscribers: false,
    combineWith: "include",
    zips: "",
    states: [],
  };

  const { control, handleSubmit, reset, setValue, watch } = useForm({
    resolver: zodResolver(LocationFilterSchema),
    mode: "onBlur",
    reValidateMode: "onBlur",
    defaultValues,
  });

  const filterSubscribers = watch("filterSubscribers");

  return (
    <form>
      {filterSubscribers ? (
        <>
          <FlexBox
            alignItems="flex-end"
            sx={{ marginBottom: theme.spacing(2) }}
          >
            <RhfSelect secondary name="combineWith" control={control}>
              <MenuItem value={"include"}>Include</MenuItem>
              <MenuItem value={"exclude"}>Exclude</MenuItem>
            </RhfSelect>
            <Typography>subscribers within the following locations:</Typography>
          </FlexBox>

          <RhfSelect
            label="States"
            fullWidth
            control={control}
            name="states"
            multiple
            bottomLabel="You may select multiple states."
          >
            {STATES.map(state => (
              <MenuItem key={state.abbreviation} value={state.abbreviation}>
                {state.name}
              </MenuItem>
            ))}
          </RhfSelect>

          <RhfTextField
            fullWidth
            name={"zips"}
            control={control}
            label="Zip codes"
            bottomLabel="You may enter multiple zip codes separated by commas."
          />
        </>
      ) : undefined}

      <FlexBox justifyContent="flex-end" gap={1}>
        {filterSubscribers ? (
          <Button
            color="primary"
            onClick={() => {
              reset();
            }}
            disabled={readOnly}
          >
            Clear filters
          </Button>
        ) : (
          <Button
            color="primary"
            onClick={() => {
              setValue("filterSubscribers", true);
            }}
            disabled={readOnly}
          >
            Filter subscribers
          </Button>
        )}

        <Button
          color="primary"
          variant="contained"
          onClick={handleSubmit(values => {
            if (values.filterSubscribers) {
              return startImport({
                variables: {
                  input: {
                    campaignId: campaign.id,
                    combineFiltersWith: values.combineWith,
                    zips: values.zips.length ? values.zips.split(",") : [],
                    states: values.states.filter(s => s !== null),
                  },
                },
              });
            } else {
              return startImport();
            }
          })}
          disabled={readOnly}
        >
          Import {filterSubscribers ? "Filtered" : "All"} Short Code Subscribers
        </Button>
      </FlexBox>
    </form>
  );
}
